import { createStore } from 'redux';
import omit from 'lodash/omit';
import { GlobalState, reducers } from 'reducers';
import { epicMiddleware, middleware } from './middleware';
import rootEpic from '../epics';
import { FetchStatus } from 'types/types';

const LOCAL_STORAGE_KEY = 'state';
const saveToLocalStorage = (state: GlobalState) => {
  try {
    const serializedState = JSON.stringify(omit(state, 'evalSession'));
    localStorage.setItem(LOCAL_STORAGE_KEY, serializedState);
  } catch (e) {
    console.error(e);
  }
};

/**
 * "Fetching" is a transient state that only makes sense while an API call
 * is in flight.  If it was persisted to localStorage (e.g. the user closed
 * the tab mid-request) it would never resolve, permanently disabling UI
 * that depends on it.  Mutates the object in-place, resetting any stale
 * "Fetching" values back to "None".
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const resetTransientFetchStatuses = (obj: any): void => {
  if (!obj || typeof obj !== 'object') return;
  for (const key of Object.keys(obj)) {
    // Matched on the value, not on the key name. Keying on `fetchStatus`
    //   silently exempted every differently-named status field: `files` gained
    //   a `requestFormStatus`, it was never reset, and a signing flow that did
    //   not reach a terminal action left the Sign button disabled *across
    //   reloads* — exactly the failure this function exists to prevent.
    if (obj[key] === FetchStatus.Fetching) {
      obj[key] = FetchStatus.None;
    } else if (typeof obj[key] === 'object' && obj[key] !== null) {
      // Arrays included: a slice holding objects would otherwise be skipped.
      resetTransientFetchStatuses(obj[key]);
    }
  }
};

const loadFromLocalStorage = (): GlobalState | undefined => {
  try {
    const serializedState = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (serializedState === null) {
      return undefined;
    }
    const state = JSON.parse(serializedState) as GlobalState;
    resetTransientFetchStatuses(state);
    return state;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

const store = createStore(reducers, loadFromLocalStorage(), middleware);

store.subscribe(() => saveToLocalStorage(store.getState()));

export default store;

// Export types for typed hooks
export type AppStore = typeof store;
export type AppDispatch = AppStore['dispatch'];

epicMiddleware.run(rootEpic);
